import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { bulkUploadService } from '../../../services/bulkUploadService';

const BulkStudentUploadModal = ({ isOpen, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [step, setStep] = useState('upload'); // upload, validate, process, complete
  const [validationResult, setValidationResult] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [uploadResults, setUploadResults] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  if (!isOpen) return null;

  const handleFileSelect = (e) => {
    const selectedFile = e?.target?.files?.[0];
    if (selectedFile) {
      if (!selectedFile?.name?.endsWith('.csv')) {
        alert('Please select a CSV file');
        return;
      }
      setFile(selectedFile);
      setStep('upload');
      setValidationResult(null);
    }
  };

  const handleValidate = async () => {
    if (!file) return;
    
    setIsProcessing(true);
    try {
      const parseResult = await bulkUploadService?.parseCSVFile(file);
      const validation = bulkUploadService?.validateCSVData(parseResult?.data);
      setValidationResult(validation);
      setStep('validate');
    } catch (error) {
      alert(error?.message || 'Failed to validate CSV file');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpload = async () => {
    if (!validationResult?.valid || validationResult?.validRows?.length === 0) return;
    
    setIsProcessing(true);
    setStep('process');
    
    try {
      const results = await bulkUploadService?.bulkCreateStudents(
        validationResult?.validRows,
        (progress) => {
          setUploadProgress(progress);
        }
      );
      
      setUploadResults(results);
      setStep('complete');
      
      // Notify parent component
      if (onSuccess) {
        onSuccess(results);
      }
    } catch (error) {
      alert(error?.message || 'Failed to upload students');
      setStep('validate');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownloadTemplate = () => {
    bulkUploadService?.downloadTemplate();
  };

  const handleDownloadResults = () => {
    if (uploadResults) {
      bulkUploadService?.exportResultsToCSV(uploadResults);
    }
  };

  const handleClose = () => {
    setFile(null);
    setStep('upload');
    setValidationResult(null);
    setUploadProgress(null);
    setUploadResults(null);
    setIsProcessing(false);
    onClose();
  };

  const renderUploadStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="Upload" size={32} color="var(--color-primary)" />
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-2">Upload Student Data</h3>
        <p className="text-muted-foreground">Upload a CSV file with student information to create accounts in bulk</p>
      </div>

      <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary transition-smooth">
        <input
          type="file"
          accept=".csv"
          onChange={handleFileSelect}
          className="hidden"
          id="csv-upload"
        />
        <label htmlFor="csv-upload" className="cursor-pointer">
          <Icon name="FileText" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
          <p className="text-foreground font-medium mb-1">Click to upload CSV file</p>
          <p className="text-sm text-muted-foreground">or drag and drop</p>
        </label>
      </div>

      {file && (
        <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
          <div className="flex items-center gap-3">
            <Icon name="FileText" size={24} color="var(--color-primary)" />
            <div>
              <p className="font-medium text-foreground">{file?.name}</p>
              <p className="text-sm text-muted-foreground">{(file?.size / 1024)?.toFixed(2)} KB</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setFile(null)}
          >
            <Icon name="X" size={16} />
          </Button>
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex gap-3">
          <Icon name="Info" size={20} color="#3b82f6" className="flex-shrink-0 mt-0.5" />
          <div className="space-y-2">
            <p className="font-medium text-blue-900">CSV Format Requirements:</p>
            <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
              <li>Required columns: email, full_name, mobile, city, state, course_interest</li>
              <li>Optional columns: exam_status, budget_range, study_mode</li>
              <li>Email must be valid and unique</li>
              <li>Mobile must be 10-digit Indian number</li>
            </ul>
            <Button
              variant="link"
              size="sm"
              iconName="Download"
              onClick={handleDownloadTemplate}
              className="text-blue-600 p-0 h-auto"
            >
              Download CSV Template
            </Button>
          </div>
        </div>
      </div>

      <div className="flex gap-3 justify-end">
        <Button variant="outline" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          onClick={handleValidate}
          disabled={!file || isProcessing}
          loading={isProcessing}
          iconName="CheckCircle"
        >
          Validate File
        </Button>
      </div>
    </div>
  );

  const renderValidateStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
          validationResult?.valid ? 'bg-success/10' : 'bg-warning/10'
        }`}>
          <Icon
            name={validationResult?.valid ? 'CheckCircle' : 'AlertTriangle'}
            size={32}
            color={validationResult?.valid ? 'var(--color-success)' : 'var(--color-warning)'}
          />
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-2">Validation Results</h3>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="bg-muted rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-foreground">{validationResult?.totalRows}</p>
          <p className="text-sm text-muted-foreground">Total Rows</p>
        </div>
        <div className="bg-success/10 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-success">{validationResult?.validCount}</p>
          <p className="text-sm text-muted-foreground">Valid</p>
        </div>
        <div className="bg-error/10 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-error">{validationResult?.errorCount}</p>
          <p className="text-sm text-muted-foreground">Errors</p>
        </div>
      </div>

      {validationResult?.errors?.length > 0 && (
        <div className="bg-error/5 border border-error/20 rounded-lg p-4 max-h-60 overflow-y-auto">
          <div className="flex items-start gap-3 mb-3">
            <Icon name="AlertCircle" size={20} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-error mb-2">Validation Errors:</p>
              <ul className="text-sm text-error space-y-1">
                {validationResult?.errors?.slice(0, 10)?.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
                {validationResult?.errors?.length > 10 && (
                  <li className="font-medium">...and {validationResult?.errors?.length - 10} more errors</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      {validationResult?.valid && (
        <div className="bg-success/5 border border-success/20 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="CheckCircle" size={20} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-success">All rows validated successfully!</p>
              <p className="text-sm text-muted-foreground mt-1">
                Ready to create {validationResult?.validCount} student accounts with auto-generated credentials.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-3 justify-end">
        <Button variant="outline" onClick={() => setStep('upload')}>
          Back
        </Button>
        <Button
          onClick={handleUpload}
          disabled={!validationResult?.valid || validationResult?.validCount === 0}
          iconName="Upload"
        >
          Upload {validationResult?.validCount} Students
        </Button>
      </div>
    </div>
  );

  const renderProcessStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
          <Icon name="Upload" size={32} color="var(--color-primary)" />
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-2">Creating Student Accounts</h3>
        <p className="text-muted-foreground">Please wait while we process the student data...</p>
      </div>

      {uploadProgress && (
        <div className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Progress</span>
            <span className="font-medium text-foreground">
              {uploadProgress?.current} / {uploadProgress?.total} ({uploadProgress?.percentage}%)
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
            <div
              className="bg-primary h-full transition-all duration-300 rounded-full"
              style={{ width: `${uploadProgress?.percentage}%` }}
            />
          </div>
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex gap-3">
          <Icon name="Info" size={20} color="#3b82f6" className="flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">What's happening:</p>
            <ul className="space-y-1 list-disc list-inside">
              <li>Creating user accounts with auto-generated passwords</li>
              <li>Generating unique student IDs</li>
              <li>Setting up student profiles</li>
              <li>Sending welcome emails (if configured)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCompleteStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="CheckCircle" size={32} color="var(--color-success)" />
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-2">Upload Complete!</h3>
        <p className="text-muted-foreground">Student accounts have been created successfully</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-success/10 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-success">{uploadResults?.successful?.length}</p>
          <p className="text-sm text-muted-foreground">Successful</p>
        </div>
        <div className="bg-error/10 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-error">{uploadResults?.failed?.length}</p>
          <p className="text-sm text-muted-foreground">Failed</p>
        </div>
      </div>

      {uploadResults?.failed?.length > 0 && (
        <div className="bg-warning/5 border border-warning/20 rounded-lg p-4 max-h-40 overflow-y-auto">
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" size={20} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-warning mb-2">Failed Accounts:</p>
              <ul className="text-sm text-muted-foreground space-y-1">
                {uploadResults?.failed?.slice(0, 5)?.map((item, index) => (
                  <li key={index}>
                    Row {item?.rowNumber}: {item?.email} - {item?.error}
                  </li>
                ))}
                {uploadResults?.failed?.length > 5 && (
                  <li className="font-medium">...and {uploadResults?.failed?.length - 5} more</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex gap-3">
          <Icon name="Download" size={20} color="#3b82f6" className="flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium text-blue-900 mb-1">Download Credentials</p>
            <p className="text-sm text-blue-800 mb-3">
              Download a CSV file containing student IDs, emails, and auto-generated passwords.
              Share this securely with students.
            </p>
            <Button
              variant="outline"
              size="sm"
              iconName="Download"
              onClick={handleDownloadResults}
            >
              Download Results CSV
            </Button>
          </div>
        </div>
      </div>

      <div className="flex gap-3 justify-end">
        <Button onClick={handleClose} iconName="CheckCircle">
          Done
        </Button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {step === 'upload' && renderUploadStep()}
          {step === 'validate' && renderValidateStep()}
          {step === 'process' && renderProcessStep()}
          {step === 'complete' && renderCompleteStep()}
        </div>
      </div>
    </div>
  );
};

export default BulkStudentUploadModal;
